/* eslint-disable no-nested-ternary */
import { Dispatch, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';

import { GridWithInlineEditRef } from 'ui-component/grids/GridWithInlineEdit';
import { getRenderCell, getRenderEditCellByType, getValueFormatter } from '../utils';
import { IRecordField } from '../types';
import {
    generateDynamicObjectPayload,
    generateDynamicPlainObjectById,
    generateLineItemRows,
    generateLineItemsPayload,
    getFieldsAsHeadersFields,
    getObjectPropertiesById,
    getOptionObjectIdsFromLineItems,
    getValueFromLineItemsData,
    mixedTypeSort
} from './utils';
import { ILineItemHeader, LineItemsFields } from './types';
import { getListIdsFromHeaders } from '../utils/headerHelpers';
import { useCustomListValues } from 'hooks/useCustomListValues';
import { useLineItemsData, useLineItemsMutations } from './hooks';
import { useDocumentViewer } from 'views/DocumentViewer/hooks/useDocumentViewer';
import { Grid, Typography } from '@mui/material';
// import { AddCircleOutline } from '@mui/icons-material';
import useIsAllowedByRecordType from 'hooks/useIsAllowedByRecordType';
import LineItemsGridSkeleton from './Grid';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { FindDynamicObjects, FindDynamicObjectsVariables, FindObjects, FindObjectsVariables } from 'views/backoffice/CustomObjects/types';
import { QUERY_FIND_DYNAMIC_OBJECTS, QUERY_FIND_OBJECT } from 'graphql/queries/customObjects';
import { OPTION_FIELD_NAME, OPTION_FIELD_OBJECT_NAME } from './constants';
import { ObjectPanel } from '../ImportDialogs/components/ObjectPanel';
import { SifOption } from '../ImportDialogs/types';
import {
    RegisterOrUpdateDynamicObject,
    RegisterOrUpdateDynamicObjectsWithValuesPayload
} from 'views/TenantProfileSettings/components/types';
import { MUTATION_REGISTER_OR_UPDATE_DYNAMIC_OBJECT } from 'graphql/mutations/dynamicObject';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { getFormattedValue } from 'ui-component/RecordView/utils';
import { ROLE_ACTIONS } from 'types/layout';

export type LineItemsGridProps = {
    isOpen: boolean;
    recordId: number;
    headers?: ILineItemHeader;
    recordTypeId?: number;
    showCustomNoRowsOverlay?: boolean;
    onChangeLineItems?: (newArr: Record<string, any>) => void;
    onDeleteLineItem?: (index: number) => void;
    height?: string | number;
    rightPosition?: number;
} & ({ manualCreation?: false } | { manualCreation: true; onSubmitCreation?: (rows: Record<string, any>[]) => void });

export type LineItemsGridRef = {
    handleAddClick: () => void;
    setNewRows: Dispatch<React.SetStateAction<Record<string, any>[]>>;
};

export const LineItemsGrid = forwardRef(
    (
        {
            isOpen,
            recordId,
            headers,
            recordTypeId,
            showCustomNoRowsOverlay,
            onChangeLineItems,
            onDeleteLineItem,
            height,
            rightPosition = 0,
            ...props
        }: LineItemsGridProps,
        ref
    ) => {
        const dispatch = useDispatch();
        const { handleSetLineItemUpdated } = useDocumentViewer();
        const gridRef = useRef<GridWithInlineEditRef | null>(null);
        const [newRows, setNewRows] = useState<Record<string, any>[]>([]);
        const [cleanSortModel, setCleanSortModel] = useState<boolean>(false);
        const [selectedOptionRowId, setSelectedOptionRowId] = useState<number>();
        const [selectedRowToEdit, setSelectedRowToEdit] = useState<Record<string, any> | null>(null);
        const originalOptionsByRowId = useRef<Record<number, SifOption[]>>({});

        const { lineItemHeaders, getLineItems, lineItemsData, loading: loadingData } = useLineItemsData(recordTypeId as number, headers);
        const { data: objectData, loading: loadingFindObjects } = useQuery<FindObjects, FindObjectsVariables>(QUERY_FIND_OBJECT, {
            variables: { data: { names: [OPTION_FIELD_OBJECT_NAME] } }
        });
        const [findDynamicObjects] = useLazyQuery<FindDynamicObjects, FindDynamicObjectsVariables>(QUERY_FIND_DYNAMIC_OBJECTS);

        const optionsFieldObjectDefinition = useMemo(() => objectData?.findObjects?.[0], [objectData]);
        const optionsFieldLineItemHeader = useMemo(
            () => lineItemHeaders?.lineItemsByTypeFileds.find((el) => el.recordAdditionalFields.name === OPTION_FIELD_NAME),
            [lineItemHeaders?.lineItemsByTypeFileds]
        );
        const optionsFieldName = useMemo(
            () => `${optionsFieldLineItemHeader?.recordAdditionalFields.name}-${optionsFieldLineItemHeader?.order}`,
            [optionsFieldLineItemHeader]
        );

        const headerListIds = useMemo(
            () => getListIdsFromHeaders(getFieldsAsHeadersFields(lineItemHeaders?.lineItemsByTypeFileds)),
            [lineItemHeaders?.lineItemsByTypeFileds]
        );
        const { createLineItem, createOrUpdateByRow, loading: loadingMutations, updateLineItem } = useLineItemsMutations(recordId);
        const [registerDynamicObject] = useMutation<RegisterOrUpdateDynamicObject, RegisterOrUpdateDynamicObjectsWithValuesPayload>(
            MUTATION_REGISTER_OR_UPDATE_DYNAMIC_OBJECT
        );

        const { loading: loadingListValues, listValuesByListId } = useCustomListValues(headerListIds);
        // TODO: adjust this to use ROLE_ACTIONS constants
        const { isAllowed: isAllowedToEditLineItems } = useIsAllowedByRecordType('edit line items', Number(recordTypeId));
        const { isAllowed: isAllowedToExportLineItems } = useIsAllowedByRecordType('export line items', Number(recordTypeId));
        const { isAllowed: isAllowedToImportLineItems } = useIsAllowedByRecordType(ROLE_ACTIONS.IMPORT_LINE_ITEMS, Number(recordTypeId));

        const headerList: GridColDef[] = useMemo(() => {
            if (!lineItemHeaders) return [];
            const sortedArr = [...lineItemHeaders.lineItemsByTypeFileds].sort((a, b) => a.order - b.order);

            return sortedArr.map(
                ({ recordAdditionalFields, order }) =>
                    ({
                        field: `${recordAdditionalFields.name}-${order}`,
                        headerName: recordAdditionalFields.name,
                        sortComparator: mixedTypeSort,
                        renderHeader: (params) => (
                            <Typography fontSize="16px" fontWeight={700} color="#000000">
                                {params.colDef.headerName}
                            </Typography>
                        ),
                        editable: `${recordAdditionalFields.name}-${order}` !== optionsFieldName,
                        valueFormatter: getValueFormatter(
                            recordAdditionalFields.name,
                            recordAdditionalFields.dataType,
                            undefined,
                            undefined,
                            recordAdditionalFields.name === OPTION_FIELD_NAME
                        ),
                        renderCell: getRenderCell(
                            recordAdditionalFields.name,
                            undefined,
                            undefined,
                            recordAdditionalFields as unknown as IRecordField,
                            recordAdditionalFields.listType?.id
                                ? listValuesByListId[Number(recordAdditionalFields.listType?.id)]
                                : undefined,
                            undefined,
                            undefined,
                            {
                                name: 'line-items',
                                onClick: (id: number) => {
                                    const isNew = selectedOptionRowId !== id;
                                    setSelectedOptionRowId((prev) => (isNew ? id : undefined));
                                    if (isNew) {
                                        const selectedRow = newRows.find((row) => +row?.id === id) || null;
                                        setSelectedRowToEdit(selectedRow);
                                    }
                                }
                            }
                        ),
                        renderEditCell: getRenderEditCellByType(
                            recordAdditionalFields.dataType,
                            recordAdditionalFields.listType?.id,
                            recordAdditionalFields.listType?.id
                                ? listValuesByListId[Number(recordAdditionalFields.listType?.id)]
                                : undefined,
                            recordAdditionalFields.listType?.dataType
                        )
                    } as GridColDef)
            );
        }, [lineItemHeaders, optionsFieldName, listValuesByListId, selectedOptionRowId, newRows]);

        const handleAddClick = () => {
            gridRef.current?.handleAddClick();
        };

        // Option handlers
        const handleChangeOption = (option: SifOption) => {
            setSelectedRowToEdit((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    [optionsFieldName]: prev[optionsFieldName]?.map((curOption: SifOption) =>
                        Number(curOption?.id) === Number(option.id) ? option : curOption
                    )
                };
            });
        };

        const handleDeleteOption = (optionId: number) => {
            setSelectedRowToEdit((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    [optionsFieldName]: prev[optionsFieldName]?.filter((curOption: SifOption) => Number(curOption?.id) !== Number(optionId))
                };
            });
        };

        const handleAddOption = () => {
            setSelectedRowToEdit((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    [optionsFieldName]: [
                        ...prev[optionsFieldName],
                        {
                            id: prev[optionsFieldName]?.length ? +prev[optionsFieldName].at(-1)?.id + 1 : 1,
                            'Option Number': '',
                            'Option Description': '',
                            'Option Group': '',
                            'Option List Up charge': ''
                        }
                    ]
                };
            });
        };

        const handleCreateOrUpdateOption = async () => {
            if (!selectedRowToEdit || !optionsFieldObjectDefinition || !selectedOptionRowId) return;

            const currentOptions = selectedRowToEdit[optionsFieldName] || [];
            const originalOptions = originalOptionsByRowId.current[selectedOptionRowId] || [];
            const objectDefinitionId = Number(optionsFieldObjectDefinition.id);

            // Generate all payloads
            const payloads = generateDynamicObjectPayload(
                currentOptions,
                originalOptions,
                objectDefinitionId,
                optionsFieldObjectDefinition.objectProperties
            );

            const objIds: number[] = [];

            // Execute creation/update for each payload
            try {
                for await (const payload of payloads) {
                    const { data } = await registerDynamicObject({
                        variables: {
                            data: payload
                        }
                    });
                    if (data?.registerOrUpdateDynamicObject.id) {
                        objIds.push(Number(data.registerOrUpdateDynamicObject.id));
                    }
                }
                await handleProcessUpdate(
                    {
                        ...selectedRowToEdit,
                        [`${optionsFieldLineItemHeader?.recordAdditionalFields.name}-${optionsFieldLineItemHeader?.order}`]:
                            JSON.stringify(objIds)
                    },
                    true
                );
            } catch (error) {
                console.error('Error creating/updating dynamic object for option:', error);
            }

            dispatch(
                openSnackbar({
                    open: true,
                    type: 'success',
                    title: 'Success',
                    subtitle: `Options updated successfully`
                })
            );

            setSelectedRowToEdit(null);
            setSelectedOptionRowId(undefined);
            setNewRows((prev) => prev.map((el) => (el.id === selectedRowToEdit.id ? selectedRowToEdit : el)));
        };

        useImperativeHandle(ref, () => ({
            handleAddClick,
            setNewRows
        }));

        useEffect(() => {
            setCleanSortModel(!showCustomNoRowsOverlay);
        }, [showCustomNoRowsOverlay]);

        const handleProcessUpdate = async (newOne: any, includeOptions: boolean = false) => {
            try {
                let rowId: string | number = 0;
                const items = Object.keys(newOne)
                    .filter((key) => !['id', 'order', includeOptions ? '' : OPTION_FIELD_NAME].includes(key))
                    .filter((key) => {
                        const newValue = newOne[key];
                        const oldData = getValueFromLineItemsData(
                            newOne.id,
                            lineItemsData?.findLineItems,
                            lineItemHeaders?.lineItemsByTypeFileds,
                            key
                        );

                        // This is needed to get the rowId even in the non created items
                        if (rowId === 0) rowId = oldData?.lineItemsRow?.id || 0;

                        return !oldData || newValue !== oldData.value;
                    })
                    .map((key) => {
                        const [fieldNameFromUpdated, fieldOrder] = key.split('-');
                        const idFromHeader = Number(
                            lineItemHeaders?.lineItemsByTypeFileds.find(
                                (el) => el.recordAdditionalFields.name === fieldNameFromUpdated && el.order === Number(fieldOrder)
                            )?.id
                        );
                        const headerData = lineItemHeaders?.lineItemsByTypeFileds.find(
                            (el) => el.recordAdditionalFields.name === fieldNameFromUpdated && el.order === Number(fieldOrder)
                        );

                        const oldData = getValueFromLineItemsData(
                            newOne.id,
                            lineItemsData?.findLineItems,
                            lineItemHeaders?.lineItemsByTypeFileds,
                            key
                        );

                        const id = Number(oldData?.id);

                        return id && idFromHeader
                            ? ({
                                  id,
                                  lineItemsByTypeFiledsId: idFromHeader,
                                  value: getFormattedValue(
                                      newOne[key],
                                      {
                                          id,
                                          dataType: headerData?.recordAdditionalFields.dataType as IRecordField['dataType']
                                      },
                                      headerData?.recordAdditionalFields.name === OPTION_FIELD_NAME
                                  )
                              } as LineItemsFields)
                            : newOne[key] && idFromHeader
                            ? ({
                                  lineItemsByTypeFiledsId: idFromHeader,
                                  value: getFormattedValue(
                                      newOne[key],
                                      {
                                          id,
                                          dataType: headerData?.recordAdditionalFields.dataType as IRecordField['dataType']
                                      },
                                      headerData?.recordAdditionalFields.name === OPTION_FIELD_NAME
                                  )
                              } as LineItemsFields)
                            : null;
                    })
                    .filter((el) => el !== null);

                const payload = {
                    index: Number(newOne.id),
                    lineItemsFields: items as LineItemsFields[],
                    recordHeaderId: recordId,
                    rowId: +rowId
                };

                if (onChangeLineItems) {
                    onChangeLineItems(payload);
                    return newOne;
                }

                if (rowId !== 0) {
                    await createOrUpdateByRow({
                        variables: {
                            data: payload
                        }
                    });
                    handleSetLineItemUpdated();
                } else {
                    const itemsWithRecordData = items.map((item) =>
                        item?.id
                            ? {
                                  ...item,
                                  id: item.id as number,
                                  index: Number(newOne.id),
                                  recordHeaderId: recordId
                              }
                            : {
                                  ...item,
                                  index: Number(newOne.id),
                                  recordHeaderId: recordId
                              }
                    );

                    if (recordId !== 0) {
                        await Promise.all(
                            itemsWithRecordData
                                .filter((el) => el !== null)
                                .map((el) =>
                                    // @ts-ignore
                                    el.id ? updateLineItem({ variables: { data: el } }) : createLineItem({ variables: { data: el } })
                                )
                        );
                        handleSetLineItemUpdated();

                        return newOne;
                    }
                }

                if (recordId !== 0) {
                    await getLineItemData();
                    handleSetLineItemUpdated();
                }

                if (!includeOptions) setNewRows((prev) => prev.map((el) => (el.id === newOne.id ? newOne : el)));

                return newOne;
            } catch (error) {
                console.log('error changing values', error);
            }
            return null;
        };

        const handleProcessCreate = async (newOne: any) => {
            try {
                const index = newRows.slice(-1)[0]?.id + 1 || 1;

                if (!lineItemHeaders) return null;

                const { items, payload } = generateLineItemsPayload(newOne, lineItemHeaders?.lineItemsByTypeFileds, recordId);

                const newWithIndex = { ...newOne, id: index };
                setNewRows((prev) => [...prev, newWithIndex]);

                if (onChangeLineItems) {
                    onChangeLineItems(payload);

                    return newOne;
                }

                if (recordId !== 0) {
                    await createOrUpdateByRow({
                        variables: { data: { index, lineItemsFields: items, recordHeaderId: recordId } }
                    });
                    handleSetLineItemUpdated();
                }

                if (recordId !== 0) {
                    await getLineItemData();
                    handleSetLineItemUpdated();
                }

                return newWithIndex;
            } catch (error) {
                console.log('error creating new line item', error);
                return null;
            }
        };

        const getDynamicObjectData = useCallback(
            async (ids: number[]) => {
                const { data } = await findDynamicObjects({ fetchPolicy: 'network-only', variables: { data: { ids } } });
                return data?.findDynamicObjects;
            },
            [findDynamicObjects]
        );

        const getLineItemData = useCallback(async () => {
            if (loadingFindObjects) return;
            // Right now only options is a dynamic object, so we need to get the options from the dynamic object
            const optionsField = lineItemHeaders?.lineItemsByTypeFileds.find(
                (el) => el.recordAdditionalFields.name.toLowerCase() === 'options'
            );

            const { data } = await getLineItems({ fetchPolicy: 'network-only', variables: { data: { recordHeadersIds: [recordId] } } });

            const dynamicObjectIds = getOptionObjectIdsFromLineItems(data?.findLineItems || [], optionsField);
            const dynamicObjectData = await getDynamicObjectData(dynamicObjectIds);

            const objectPropertiesById = getObjectPropertiesById(optionsFieldObjectDefinition?.objectProperties);

            const dynamicObjectsById = generateDynamicPlainObjectById(dynamicObjectData, objectPropertiesById);

            const rows = generateLineItemRows(lineItemHeaders?.lineItemsByTypeFileds, data?.findLineItems, dynamicObjectsById);

            // Save original options for each row to track changes
            rows.forEach((row) => {
                if (row[optionsFieldName]) {
                    originalOptionsByRowId.current[+row.id] = JSON.parse(JSON.stringify(row[optionsFieldName]));
                }
            });

            setNewRows(rows);
        }, [
            loadingFindObjects,
            lineItemHeaders?.lineItemsByTypeFileds,
            getLineItems,
            recordId,
            getDynamicObjectData,
            optionsFieldObjectDefinition?.objectProperties,
            optionsFieldName
        ]);

        useEffect(() => {
            getLineItemData();
        }, [getLineItemData]);

        return (
            <Grid container sx={{ height: height || 'calc(100% - 52px)' }}>
                <Grid
                    item
                    xs={selectedOptionRowId ? 'auto' : 12}
                    container
                    spacing={1}
                    height="100%"
                    sx={{
                        position: 'relative',
                        width: selectedOptionRowId ? 'calc(100% - 378px) !important' : undefined
                    }}
                >
                    <LineItemsGridSkeleton
                        gridRef={gridRef}
                        handleProcessCreate={handleProcessCreate}
                        handleProcessUpdate={handleProcessUpdate}
                        onDeleteLineItem={onDeleteLineItem as (id: number) => void}
                        loadingData={loadingMutations || loadingData || loadingListValues || loadingFindObjects}
                        columns={headerList}
                        rows={newRows}
                        handleAddClick={handleAddClick}
                        isAllowedToEditLineItems={!!isAllowedToEditLineItems}
                        isAllowedToExportLineItems={!!isAllowedToExportLineItems}
                        handleOnRefetch={getLineItemData}
                        baseIndexForTheCreation={newRows.slice(-1)[0]?.id + 1}
                        recordTypeId={recordTypeId as number}
                        recordId={recordId}
                        toolbarProps={{
                            manualCreation: props.manualCreation,
                            onSubmitCreation: props.manualCreation ? props.onSubmitCreation : undefined
                        }}
                        cleanSortModel={cleanSortModel}
                        shouldImport={!!isAllowedToImportLineItems}
                    />
                </Grid>
                {selectedOptionRowId && selectedRowToEdit && optionsFieldObjectDefinition && (
                    <Grid item xs="auto" container spacing={1} height="100%" sx={{ position: 'relative' }}>
                        <ObjectPanel
                            selectedRow={selectedRowToEdit}
                            handleChangeOption={handleChangeOption}
                            handleDeleteOption={handleDeleteOption}
                            handleAddOption={handleAddOption}
                            properties={optionsFieldObjectDefinition.objectProperties}
                            fieldName={`${optionsFieldLineItemHeader?.recordAdditionalFields.name}-${optionsFieldLineItemHeader?.order}`}
                            onSave={handleCreateOrUpdateOption}
                        />
                    </Grid>
                )}
            </Grid>
        );
    }
);

export default LineItemsGrid;
